import classNames from 'classnames'
import { type ReactNode } from 'react'

import { ArrowDown, ArrowUp } from '../icon/solar-icons.js'
import './portal-data-table.css'

// ── Column definition ──

export interface PortalDataTableColumn<T> {
    /** Unique key for this column. */
    key: string
    /** Header label displayed in <th>. */
    header: string
    /** Render the cell content for a given row. */
    render: (row: T, index: number) => ReactNode
    /** Horizontal alignment: 'left' (default), 'center', or 'right'. */
    align?: 'left' | 'center' | 'right'
    /** If true, long content will be truncated with ellipsis. */
    truncate?: boolean
    /** If true, this column header is sortable. */
    sortable?: boolean
    /** Optional min-width for the column. */
    minWidth?: number
    /**
     * Optional fixed width for the column (px). Give every column but one a
     * width when several tables stacked on a page must share one column grid
     * (e.g. the per-project task tables on /me/tasks) — the width-less column
     * absorbs the remaining space, so the others line up across tables.
     */
    width?: number
}

// ── Sort state ──

export interface SortState {
    key: string
    direction: 'asc' | 'desc'
}

// ── Component props ──

export interface PortalDataTableProps<T> {
    /** Column definitions. */
    columns: PortalDataTableColumn<T>[]
    /** Array of row data. */
    rows: T[]
    /** Unique key extractor for React keys. */
    rowKey: (row: T, index: number) => string | number
    /** Optional click handler for a row. */
    onRowClick?: (row: T, index: number) => void
    /** Optional class name appended to the row <tr>. */
    rowClassName?: (row: T, index: number) => string | undefined
    /** Sort state and handler. */
    sort?: SortState
    onSortChange?: (sort: SortState) => void
    /** Message to display when rows is empty. */
    emptyMessage?: string
    /** Optional icon node rendered above the empty message. */
    emptyIcon?: ReactNode
    /** Whether data is currently loading. */
    isLoading?: boolean
    /** Optional loading message. */
    loadingMessage?: string
    /** Optional children rendered after the table (e.g. pagination). */
    children?: ReactNode
}

export const PortalDataTable = <T,>({
    columns,
    rows,
    rowKey,
    onRowClick,
    rowClassName,
    sort,
    onSortChange,
    emptyMessage,
    emptyIcon,
    isLoading,
    loadingMessage,
    children,
}: PortalDataTableProps<T>) => {
    const handleSort = (col: PortalDataTableColumn<T>) => {
        if (!col.sortable || !onSortChange) return
        const direction = sort?.key === col.key && sort.direction === 'asc' ? 'desc' : 'asc'
        onSortChange({ key: col.key, direction })
    }

    if (isLoading) {
        return <div className="portal-data-table__loading">{loadingMessage ?? '…'}</div>
    }

    if (rows.length === 0) {
        return (
            <div className="portal-data-table__empty">
                {emptyIcon ? (
                    <div className="portal-data-table__empty-icon">{emptyIcon}</div>
                ) : null}
                <div className="portal-data-table__empty-text">{emptyMessage ?? '—'}</div>
            </div>
        )
    }

    return (
        <>
            <div className="portal-data-table__wrap">
                <table className="portal-data-table">
                    <thead>
                        <tr>
                            {columns.map((col) => {
                                const isSorted = sort?.key === col.key
                                return (
                                    <th
                                        key={col.key}
                                        className={classNames({
                                            'is-sortable': col.sortable,
                                            'is-sorted': isSorted,
                                            'is-right': col.align === 'right',
                                            'is-center': col.align === 'center',
                                        })}
                                        onClick={col.sortable ? () => handleSort(col) : undefined}
                                        style={
                                            col.minWidth || col.width
                                                ? { minWidth: col.minWidth, width: col.width }
                                                : undefined
                                        }
                                    >
                                        {col.header}
                                        {col.sortable ? (
                                            <span className="portal-data-table__sort-icon">
                                                {isSorted && sort?.direction === 'desc' ? (
                                                    <ArrowDown size={12} />
                                                ) : (
                                                    <ArrowUp size={12} />
                                                )}
                                            </span>
                                        ) : null}
                                    </th>
                                )
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => (
                            <tr
                                key={rowKey(row, i)}
                                className={classNames(
                                    'portal-data-table__row',
                                    onRowClick && 'is-clickable',
                                    rowClassName?.(row, i),
                                )}
                                onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                            >
                                {columns.map((col) => (
                                    <td
                                        key={col.key}
                                        className={classNames({
                                            'is-right': col.align === 'right',
                                            'is-center': col.align === 'center',
                                            'is-truncate': col.truncate,
                                        })}
                                    >
                                        {col.render(row, i)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {children}
        </>
    )
}
